import { withoutTrailingSlash } from 'ufo'

/**
 * Single source of truth for the canonical site origin.
 *
 * Reads `site.url` from nuxt.config (nuxt-site-config, shared with @nuxtjs/sitemap
 * and @nuxtjs/robots) so canonicals, og:image URLs and the sitemap can never
 * disagree about the host. Canonical host is the `www` subdomain — the apex
 * redirects there.
 */
export const useSiteUrl = () => {
  const siteUrl = withoutTrailingSlash(useSiteConfig().url)

  /**
   * Turns a root-relative path into an absolute URL. Open Graph requires
   * absolute URLs — LinkedIn and Slack will not resolve a root-relative one.
   * Returns undefined for empty input so unhead drops the tag instead of
   * emitting content="".
   */
  const absUrl = (path?: string | null): string | undefined => {
    if (!path) return undefined
    if (/^https?:\/\//i.test(path)) return path
    if (path.startsWith('//')) return `https:${path}`
    return `${siteUrl}/${path.replace(/^\/+/, '')}`
  }

  /** Absolute canonical URL for a route path. Root keeps its slash, others drop it. */
  const canonicalFor = (path: string): string =>
    `${siteUrl}${path === '/' ? '/' : withoutTrailingSlash(path)}`

  return { siteUrl, absUrl, canonicalFor }
}
